import { writable, get } from 'svelte/store';
import type { Writable, Updater } from 'svelte/store';
import { jwtDecode } from 'jwt-decode';
import site from './site';

// Types
export interface TokenData {
	lastName: string;
	firstName: string;
	userUUID: string;
	username: string;
	email: string;
	isActive: boolean;
	roles: { name: string; roleUUID: string }[];
	iat: number;
	exp: number;
}

export interface UserProfile {
	lastName: string;
	firstName: string;
	userUUID: string;
	email: string;
	profilePictureURL?: string;
}

/**
 * Writable store backed by localStorage.
 *
 * Values are JSON-encoded by default, so any serialisable type works — booleans
 * for collapsed sidebar sections, objects for view preferences. Pass
 * `storeAsJson = false` for plain strings written by other code (e.g. the
 * `color-theme` key, which is also read by the pre-hydration theme script).
 */
export function storable<T>(key: string, data: T, storeAsJson: boolean = true): Writable<T> {
	const store: Writable<T> = writable(data);

	const stored = localStorage.getItem(key);
	if (stored != null) {
		store.set(storeAsJson ? JSON.parse(stored) : (stored as unknown as T));
	}

	return {
		subscribe: store.subscribe,
		set: (value: T) => {
			localStorage.setItem(key, storeAsJson ? JSON.stringify(value) : `${value}`);
			store.set(value);
		},
		update: (fnCallback: Updater<T>) => {
			const updatedStore: T = fnCallback(get(store));

			localStorage.setItem(key, storeAsJson ? JSON.stringify(updatedStore) : `${updatedStore}`);
			store.set(updatedStore);
		}
	};
}

// Stores
export const isAuthorized = writable(false);
export const isAuthorizationPending = writable(true);
export const colorTheme = storable<'dark' | 'light'>('color-theme', 'dark', false);
export const userProfileAvailable = writable(false);

/** Query parameter AbstractID appends to returnURL to hand the JWT back. */
const TOKEN_PARAM = 'token';

/**
 * The paths this client used to serve to **customers**, before the storefront and the account
 * pages moved to AbstractID's management app under `/Store` (see "Abstract Store (storefront)" in
 * the repository's `CLAUDE.md`).
 *
 * They are still out there — in receipts and dunning emails already sent, in bookmarks, in links
 * other sites made — and a customer following one must land on the page it named, not on this
 * console's sign-in followed by a page they may not see. The Store server's `store.publicURL` now
 * points at AbstractID, so every link it builds from here on goes there directly; this list is for
 * the ones that already left.
 *
 * A `*` matches one or more path segments.
 */
export const FORMER_CUSTOMER_ROUTES: readonly string[] = [
	'/',
	'/Products',
	'/Products/*',
	'/Product/*',
	'/Checkout/*',
	'/Account',
	'/Account/*'
];

/** Turns a route pattern into an anchored regular expression. `*` spans path segments. */
function patternToRegExp(pattern: string): RegExp {
	const escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.+');
	return new RegExp(`^${escaped}/?$`);
}

/**
 * Whether a path is one a customer reached on this origin before the move. Trailing slashes are
 * tolerated, because a link with one names the same page.
 */
export function isFormerCustomerPath(pathname: string): boolean {
	const path = pathname.length === 0 ? '/' : pathname;
	return FORMER_CUSTOMER_ROUTES.some(pattern => patternToRegExp(pattern).test(path));
}

/**
 * Where on AbstractID somebody who has no business in this console belongs.
 *
 * A former customer path maps to **the same sub-path under AbstractID's `/Store`** — AbstractID
 * serves exactly those paths there, which is what lets the Store server's emailed links move by
 * changing `store.publicURL` alone — with its query string, so `/Checkout/Return?session=…` still
 * names the session. `/` maps to `/Store` itself. Anything else — an admin path, a typo — goes to
 * `/Store` too: whoever is being sent away has nothing on this origin, and the storefront is the
 * one useful place to put them.
 *
 * The `token` parameter is stripped: it is this client's hand-off from AbstractID, and forwarding
 * it would leave a session token in another page's address bar and in its referrer.
 */
export function customerPathAtAbstractID(pathname: string, search: string = ''): string {
	const storeRoot = `${site.idBaseURL}/Store`;
	if (!isFormerCustomerPath(pathname)) return storeRoot;

	const path = pathname.replace(/\/+$/, '');
	const query = new URLSearchParams(search);
	query.delete(TOKEN_PARAM);
	const remaining = query.toString();
	return `${storeRoot}${path}${remaining.length > 0 ? `?${remaining}` : ''}`;
}

// Token storage key
const TOKEN_KEY = 'jwt2';

/**
 * Builds an AbstractID authorization URL.
 *
 * The provider's `/authorize` page requires BOTH parameters and refuses the
 * request without them ("Missing application context" / "Return URL is
 * required for authorization"). The parameter names are `app` and `returnURL`
 * — see AbstractID's `pageComponents/auth/Authorize.svelte`, which reads them
 * verbatim. Constructed in one place so the two callers — sign-in and
 * sign-out — can never drift apart.
 *
 * Do NOT resurrect the old `/login?verification_url=…` form: AbstractID only
 * still honours it through a legacy shim in its `index.html` that rewrites it
 * onto this URL.
 *
 * @param returnURL Absolute URL AbstractID sends the user back to, with the token appended.
 * @param extra Additional query parameters, e.g. `{ logout: 'true' }` to revoke the session.
 */
function makeAuthorizeURL(returnURL: string, extra: Record<string, string> = {}): string {
	const params = new URLSearchParams({
		app: site.appID,
		returnURL
	});

	for (const [key, value] of Object.entries(extra)) {
		params.set(key, value);
	}

	return `${site.idBaseURL}/authorize?${params.toString()}`;
}

/**
 * Builds the URL AbstractID sends the user back to after authenticating.
 *
 * Keeps origin + path + query, drops the hash. The router is history-mode
 * (`Router.svelte` `getLocation()` reads `window.location.pathname`), so the
 * path and query ARE the current route — preserving them is what makes deep
 * links survive the login round-trip.
 *
 * The hash must go, and this is the subtle part: AbstractID appends the token
 * to the END of returnURL (`Authorize.svelte`: `returnURL + separator +
 * 'token=' + …`). With a hash still attached the append would land inside the
 * fragment — `…/Users#x?token=…` — leaving `window.location.search` empty, so
 * `initialize()` would never see the token and would bounce back to login in a
 * loop. Any stale `token` is stripped for the same reason it exists: it was
 * put there by the previous hand-off.
 */
function makeReturnURL(url: string): string {
	try {
		const parsed = new URL(url, document.location.origin);
		parsed.hash = '';
		parsed.searchParams.delete(TOKEN_PARAM);
		return parsed.toString();
	} catch {
		return site.baseURL;
	}
}

// AbstractID module
const AbstractID = {
	tokenData: null as TokenData | null,
	userProfilePictureURL: '' as string,
	refreshInterval: null as ReturnType<typeof setInterval> | null,

	/**
	 * Whether this session may use the console at all.
	 *
	 * The admin role **is** the gate: every page here is administration and every endpoint behind
	 * it is `adminAuth`. There used to be a second, optional `accessRole` beside it, from when the
	 * client also served customers and "may enter the app" and "may administer it" were different
	 * questions. They are one question now, and the server never enforced the access role anyway —
	 * an admin missing it would have been turned away by a check nothing backed.
	 */
	get isAdministrator(): boolean {
		if (!this.tokenData) return false;
		if (!site.adminRole || site.adminRole.length === 0) return false;
		return this.tokenData.roles?.some(r => r.name === site.adminRole) ?? false;
	},

	getToken(): string | null {
		return localStorage.getItem(TOKEN_KEY);
	},

	setToken(tokenStringValue: string): void {
		try {
			this.tokenData = jwtDecode<TokenData>(tokenStringValue);
			localStorage.setItem(TOKEN_KEY, tokenStringValue);

			// Fetch profile picture if not already available
			if (!get(userProfileAvailable)) {
				fetch(`${site.backend.idBaseURL}/user-backend/user/${this.tokenData!.userUUID}/profile`)
					.then((response) => response.json())
					.then((profileJson) => {
						if (profileJson.data?.profilePictureURL?.length > 0) {
							this.userProfilePictureURL = profileJson.data.profilePictureURL;
							userProfileAvailable.set(true);
						}
					})
					.catch(() => {});
			}
		} catch {
			this.tokenData = null;
			localStorage.removeItem(TOKEN_KEY);
		}
	},

	clearToken(): void {
		localStorage.removeItem(TOKEN_KEY);
		this.tokenData = null;
	},

	/**
	 * The one place this app leaves for another origin.
	 *
	 * Behind a method rather than inline, because `window.location` is an *unforgeable* interface:
	 * neither it nor its `href` can be redefined, so there is no way to observe an assignment to
	 * it from outside. Every sign-in, sign-out and send-to-AbstractID redirect goes through here,
	 * which is what makes "was a customer following an old link sent to the right page" a question
	 * a test can ask at all.
	 */
	navigateTo(url: string): void {
		window.location.href = url;
	},

	navigateToLogin(redirectURL?: string): void {
		this.navigateTo(makeAuthorizeURL(makeReturnURL(redirectURL ?? document.location.href)));
	},

	/**
	 * User-initiated sign-out. Clears the local token and hands off to AbstractID
	 * with `logout=true` so the session is revoked there too — otherwise the next
	 * sign-in silently reuses the old identity instead of prompting.
	 */
	signOut(): void {
		if (this.refreshInterval) {
			clearInterval(this.refreshInterval);
			this.refreshInterval = null;
		}
		this.clearToken();
		isAuthorized.set(false);
		this.navigateTo(makeAuthorizeURL(site.baseURL, { logout: 'true' }));
	},

	refreshToken(onError?: (error: { status: number; statusText: string }) => void): void {
		const tokenString = this.getToken();
		if (!tokenString) return;

		const xhr = new XMLHttpRequest();
		xhr.open("POST", `${site.backend.idBaseURL}/user-backend/auth/refresh`);
		xhr.setRequestHeader('Content-type', 'application/json');
		xhr.onload = () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				const newToken = JSON.parse(xhr.response);
				if (newToken?.data?.accessToken?.length > 0) {
					this.setToken(newToken.data.accessToken);
				}
			} else {
				if (onError) onError({ status: xhr.status, statusText: xhr.statusText });
			}
		};
		xhr.onerror = () => {
			if (onError) onError({ status: xhr.status, statusText: xhr.statusText });
		};
		xhr.send(JSON.stringify({ token: tokenString }));
	},

	/**
	 * Starts the ten-second refresh loop, cancelling any loop already running.
	 *
	 * A method of its own rather than inline in `initialize()` so the loop can be restarted
	 * without re-running the boot-time redirect decisions.
	 *
	 * @param onExpired Run when the provider refuses to refresh — the token is gone for good.
	 */
	startTokenRefresh(onExpired: () => void): void {
		const tokenRefreshIntervalInSeconds = 10;

		const fnCancelAutomaticTokenRefresh = () => {
			if (this.refreshInterval) {
				clearInterval(this.refreshInterval);
				this.refreshInterval = null;
			}
		};

		const onTokenError = () => {
			fnCancelAutomaticTokenRefresh();
			onExpired();
		};

		fnCancelAutomaticTokenRefresh();
		this.refreshInterval = setInterval(() => {
			this.refreshToken(onTokenError);
		}, tokenRefreshIntervalInSeconds * 1000);
		this.refreshToken(onTokenError);
	},

	/**
	 * Resolves the session at boot, and decides whether this console renders at all.
	 *
	 * Three outcomes, and only the first renders anything:
	 *
	 * - **A valid token carrying the admin role** — signed in; the refresh loop starts.
	 * - **A valid token without it** — sent to AbstractID's storefront, at the page they were
	 *   trying to reach when it was a former customer page (`customerPathAtAbstractID`). This was
	 *   an "Access Denied" alert while the client also served customers; with the customer pages
	 *   gone a non-admin can do nothing here, and the most likely reason they arrived is an old
	 *   link to a page that now lives there.
	 * - **No usable token** — on a former customer path, sent straight to the same AbstractID page
	 *   **without** a sign-in round trip here first: AbstractID signs people in for its own pages,
	 *   and bouncing a customer through this console's sign-in to reach the storefront would ask
	 *   them to authorise an admin tool they will never use. Anywhere else, the ordinary sign-in
	 *   redirect, which is what guards every admin page without a guard in each one.
	 *
	 * `isAuthorizationPending` stays `true` on both redirect paths, so the shell shows its spinner
	 * rather than a router for the moment before the browser leaves.
	 *
	 * @returns Whether the console should render — `false` when the page is navigating away.
	 */
	initialize(): boolean {
		// Check for token in URL (callback from AbstractID)
		const searchParams = new URLSearchParams(window.location.search);
		const tokenStringValue = searchParams.get(TOKEN_PARAM);
		if (tokenStringValue && tokenStringValue.length > 0) {
			this.setToken(tokenStringValue);

			// Strip only the token from the URL, so it isn't left in the address
			// bar or leaked via Referer. Any other query parameters belong to the
			// route the user deep-linked to and must survive.
			searchParams.delete(TOKEN_PARAM);
			const remaining = searchParams.toString();
			window.history.replaceState(
				{},
				'',
				`${window.location.pathname}${remaining ? `?${remaining}` : ''}${window.location.hash}`
			);
		}

		this.applyTheme();

		/**
		 * Leaves for wherever someone who may not use this console belongs.
		 *
		 * **Signed out, everybody signs in first** — whatever the path. Who may use this console is
		 * a fact about the account, and nothing about a signed-out visitor answers it: forwarding a
		 * former customer path straight to AbstractID decided "customer" from the URL alone, and sent
		 * an administrator following an old link out of the console without ever asking who they were.
		 * Signed in and not an administrator, they are forwarded to AbstractID, to the page the old
		 * URL meant where it was a customer page (`customerPathAtAbstractID`).
		 */
		const fnSendAway = (signedIn: boolean): false => {
			const { pathname, search } = window.location;
			if (signedIn) {
				this.navigateTo(customerPathAtAbstractID(pathname, search));
			} else {
				this.navigateToLogin();
			}
			return false;
		};

		const initialToken = this.getToken();
		if (initialToken) this.setToken(initialToken);

		const expirationDate = new Date(0);
		expirationDate.setUTCSeconds(this.tokenData?.exp ?? 0);

		if (!this.tokenData || expirationDate < new Date()) {
			this.clearToken();
			isAuthorized.set(false);
			return fnSendAway(false);
		}

		if (!this.isAdministrator) {
			return fnSendAway(true);
		}

		this.startTokenRefresh(() => {
			this.clearToken();
			isAuthorized.set(false);
			this.navigateToLogin();
		});

		isAuthorized.set(true);
		isAuthorizationPending.set(false);
		return true;
	},

	/**
	 * The persisted theme, applied before `initialize()` decides anything — including on the
	 * paths that navigate away, so the spinner shown meanwhile is not a white flash in dark mode.
	 */
	applyTheme(): void {
		const savedTheme = localStorage.getItem('color-theme');
		if (!savedTheme) {
			colorTheme.set('dark');
		}
		document.documentElement.classList.toggle('dark', get(colorTheme) === 'dark');
	},

	logout(): void {
		if (this.refreshInterval) {
			clearInterval(this.refreshInterval);
			this.refreshInterval = null;
		}
		this.clearToken();
		isAuthorized.set(false);
		this.navigateToLogin();
	},

	openManagementPortal(): void {
		window.open(`${site.idBaseURL}/manage`, '_blank');
	},

	// Authenticated fetch helpers
	authenticatedFetch(url: string): Promise<Response> {
		const token = this.getToken();
		if (!token) {
			throw new Error("Cannot perform authenticated fetch (Reason: User is not authenticated.)");
		}
		return fetch(url, {
			headers: {
				'X-AbstractStore-UserToken': token,
			},
			signal: AbortSignal.timeout(30_000)
		});
	},

	authenticatedPost(url: string, contentType: string | undefined, body: BodyInit): Promise<Response> {
		const token = this.getToken();
		if (!token) {
			throw new Error("Cannot perform authenticated fetch (Reason: User is not authenticated.)");
		}

		const headers: Record<string, string> = {
			'X-AbstractStore-UserToken': token,
		};

		if (contentType) {
			headers['Content-Type'] = contentType;
		}

		return fetch(url, {
			method: 'POST',
			headers,
			body,
			signal: AbortSignal.timeout(30_000)
		});
	},

	authenticatedPut(url: string, contentType: string, body: BodyInit): Promise<Response> {
		const token = this.getToken();
		if (!token) {
			throw new Error("Cannot perform authenticated fetch (Reason: User is not authenticated.)");
		}

		const headers: Record<string, string> = {
			'X-AbstractStore-UserToken': token,
		};

		if (contentType) {
			headers['Content-Type'] = contentType;
		}

		return fetch(url, {
			method: 'PUT',
			headers,
			body,
			signal: AbortSignal.timeout(30_000)
		});
	},

	authenticatedDelete(url: string): Promise<Response> {
		const token = this.getToken();
		if (!token) {
			throw new Error("Cannot perform authenticated fetch (Reason: User is not authenticated.)");
		}
		return fetch(url, {
			method: 'DELETE',
			headers: {
				'X-AbstractStore-UserToken': token,
			},
			signal: AbortSignal.timeout(30_000)
		});
	}
};

export default AbstractID;
